/**
 * The API reports integration failures with a stable code, but in two different envelopes.
 * Basket and reservation failures come from IntegrationException, which serialises its context;
 * the JsonProblemAssembler pipeline puts the code in `meta` instead. Callers should not have to
 * know which endpoint they hit, so read both.
 *
 * Codes are documented on the OneJourney v1 integration error codes page.
 */

export const INTEGRATION_ERROR_CODES = [
  'uex000', // UNEXPECTED_ERROR
  'avl000', // AVAILABILITY_FAILURE
  'avl001', // NO_AVAILABILITY
  'avl002', // AVAILABILITY_NOT_ALLOWED
  'gst000', // GUEST_FAILURE
  'gst001', // GUEST_RESERVATION_OVERLAP
  'gst002', // GUEST_GENDER_NOT_ALLOWED
  'gst003', // GUEST_BOOKING_OVERLAP
  'rsv000', // RESERVATION_FAILURE
  'rsv001', // RESERVATION_DOES_NOT_MATCH
  'rsv002', // RESERVATION_OUT_OF_BOUNDS
  'rsv003', // RESERVATION_UNSUCCESSFUL
  'ext000', // EXTERNAL_FAILURE
  'ext001', // EXTERNAL_EXCEPTION
  'ext001a', // EXTERNAL_SETUP
  'ext002', // ITEM_EXCEPTION
  'ext003', // RESERVATION_ALREADY_EXISTS
  'ext004', // ITEM_CANNOT_BE_USED
  'ext005', // STAFF_NOT_FOUND
  'ext006', // INTEGRATION_UNAVAILABLE
] as const;

export type IntegrationErrorCode = (typeof INTEGRATION_ERROR_CODES)[number];

/** We could not reach the integration, so we do not know whether there is availability. */
export const INTEGRATION_UNAVAILABLE: IntegrationErrorCode = 'ext006';

export const GUEST_BOOKING_OVERLAP: IntegrationErrorCode = 'gst003';

type ErrorLike = {
  response?: {
    data?: {
      context?: { integrationCodes?: unknown };
      meta?: { code?: unknown };
    };
  };
};

function isKnownCode(value: unknown): value is IntegrationErrorCode {
  return (
    typeof value === 'string' && (INTEGRATION_ERROR_CODES as readonly string[]).includes(value)
  );
}

export function getIntegrationErrorCode(error: unknown): IntegrationErrorCode | undefined {
  const data = (error as ErrorLike)?.response?.data;
  if (!data) return undefined;

  const fromContext = data.context?.integrationCodes;
  if (Array.isArray(fromContext)) {
    const match = fromContext.find(isKnownCode);
    if (match) return match;
  }

  return isKnownCode(data.meta?.code) ? data.meta.code : undefined;
}

export function isIntegrationUnavailable(error: unknown): boolean {
  return getIntegrationErrorCode(error) === INTEGRATION_UNAVAILABLE;
}
